import axios from "axios";
import { getCurrentSessionIdToken } from "../queries/AuthToken";
import { Answer } from "../types/answer";
import { QuestionType } from "../types/checklist";
import { errorToast, successToast } from "@idwal/idwal-react-components";

const getInspectionApiHeaders = async () => {
    const token = await getCurrentSessionIdToken();
    return {
        "Content-Type": "application/json",
        "Authorization": 'Bearer ' + token
    };
};

export const getAnswers = async (inspectionId: string): Promise<Answer[] | undefined> => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${inspectionId}`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.get(url, { headers });

        if (response.data?.answers) {
            // Transform object keyed by questionId to array
            const answersObj = response.data.answers;
            return Object.entries(answersObj).map(([questionId, answerData]): Answer => {
                const data = answerData as Record<string, unknown>;
                return {
                    answerId: questionId,
                    inspectionId,
                    questionId,
                    questionText: data.questionText as string | null,
                    value: data.value,
                    dataDictionaryId: data.dataDictionaryId as string | null,
                    createdAt: data.timestamp as string | null,
                    updatedAt: data.syncedAt as string | null,
                    questionType: data.questionType as QuestionType | null | undefined,
                    isNotable: data.isNotable as boolean | undefined,
                    inspectionNumber: data.inspectionNumber,
                };
            });
        }
    } catch {
        errorToast(`Failed to get answers for inspection: ${inspectionId}`);
    }
};

export const updateAnswer = async (answer: Answer) => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${answer.inspectionId}/answers`;

    const headers = await getInspectionApiHeaders();

    // Format as object keyed by questionId (as expected by syncAnswers Lambda)
    const data = {
        answers: {
            [answer.questionId]: {
                value: answer.value,
                dataDictionaryId: answer.dataDictionaryId,
                questionText: answer.questionText,
                questionType: answer.questionType,
                timestamp: new Date().toISOString(),
            }
        }
    };

    try {
        const response = await axios.post(url, data, { headers });

        if (response.status === 200) {
            successToast("Successfully updated answer");
            return true;
        }
    } catch {
        errorToast("Failed to update answer");
    }

    return false;
};

export const getInspectionsList = async () => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.get(url, { headers });

        if (response.data?.inspections) {
            // Map inspectionId to id for compatibility with existing Inspection type
            return response.data.inspections.map((inspection: Record<string, unknown>) => ({
                ...inspection,
                id: inspection.inspectionId,
            }));
        }
    } catch {
        errorToast("Failed to get inspections list");
    }
};
